import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FaCheckCircle, FaExclamationCircle, FaGavel } from 'react-icons/fa';
import './TermsPage.css';

const TermsPage = () => {
  const lastUpdated = 'March 1, 2024';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="terms-page"
    >
      <Helmet>
        <title>Terms of Service | ZustandTech</title>
        <meta
          name="description"
          content="Read the terms of service for ZustandTech (Zustand Tech Software Private Limited) governing the use of our website and software development services."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.zustandtech.com/terms" />
      </Helmet>

      <section className="terms-content">
        <div className="container">
          {/* Page Heading */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="page-heading"
          >
            <h1>Terms of <span className="gradient-text">Service</span></h1>
            <p>Please read these terms carefully before using our services</p>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="last-updated"
            >
              Last Updated: {lastUpdated}
            </motion.div>
          </motion.div>

          <div className="terms-grid">
            {/* Sidebar Navigation */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="terms-sidebar"
            >
              <h3>Quick Navigation</h3>
              <ul>
                <li><a href="#agreement">Acceptance of Terms</a></li>
                <li><a href="#services">Description of Services</a></li>
                <li><a href="#intellectual">Intellectual Property</a></li>
                <li><a href="#user-obligations">User Obligations</a></li>
                <li><a href="#fees">Fees and Payments</a></li>
                <li><a href="#confidentiality">Confidentiality</a></li>
                <li><a href="#warranties">Warranties and Disclaimers</a></li>
                <li><a href="#limitation">Limitation of Liability</a></li>
                <li><a href="#termination">Termination</a></li>
                <li><a href="#governing">Governing Law</a></li>
                <li><a href="#contact">Contact Information</a></li>
              </ul>
            </motion.div>

            {/* Main Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="terms-main"
            >
              <div className="terms-section" id="agreement">
                <h2>1. Acceptance of Terms</h2>
                <p>
                  Welcome to zustandtech. By accessing or using our website and services, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our services.
                </p>
                <p>
                  These Terms constitute a legally binding agreement between you ("Client" or "User") and zustandtech ("Company," "we," "us," or "our"). By using our services, you represent that you are at least 18 years old and have the legal capacity to enter into these Terms.
                </p>
              </div>

              <div className="terms-section" id="services">
                <h2>2. Description of Services</h2>
                <p>
                ZustandTech provides software development, consulting, and related technology services ("Services"). These Services may include but are not limited to:
                </p>
                <ul>
                  <li>Web and mobile application development</li>
                  <li>Cloud infrastructure setup and management</li>
                  <li>AI and machine learning solutions</li>
                  <li>Digital transformation consulting</li>
                  <li>Maintenance and support services</li>
                  <li>Technology training and workshops</li>
                </ul>
                <p>
                  The specific scope of work, deliverables, timelines, and fees for each project will be outlined in a separate Statement of Work (SOW) or Project Proposal that will be incorporated into these Terms by reference.
                </p>
              </div>

              <div className="terms-section" id="intellectual">
                <h2>3. Intellectual Property Rights</h2>
                
                <h3>3.1 Ownership of Work Product</h3>
                <p>
                  Upon full payment of all fees, the Client shall own all rights, title, and interest in and to the deliverables specifically developed for the Client under a project ("Work Product"). The Company assigns to the Client all copyrights and other intellectual property rights in the Work Product.
                </p>

                <h3>3.2 Company's Pre-existing Materials</h3>
                <p>
                  The Company retains all rights to its pre-existing materials, tools, libraries, and methodologies used in providing the Services. The Client receives a non-exclusive, perpetual license to use such materials solely as incorporated into the Work Product.
                </p>

                <h3>3.3 Third-Party Materials</h3>
                <p>
                  The Work Product may include third-party materials (open-source libraries, frameworks, etc.) that are subject to their own licenses. The Client agrees to comply with all such third-party license terms.
                </p>

                <h3>3.4 Company's Right to Display</h3>
                <p>
                  The Company reserves the right to display and link to the Work Product as part of its portfolio and marketing materials, unless otherwise agreed in writing.
                </p>
              </div>

              <div className="terms-section" id="user-obligations">
                <h2>4. User Obligations</h2>
                <p>As a user of our Services, you agree to:</p>
                <ul>
                  <li>Provide accurate, current, and complete information</li>
                  <li>Maintain the security of your account credentials</li>
                  <li>Promptly review and provide feedback on deliverables</li>
                  <li>Provide necessary access to your systems and personnel</li>
                  <li>Comply with all applicable laws and regulations</li>
                  <li>Not use our Services for any illegal or unauthorized purpose</li>
                  <li>Not interfere with or disrupt the integrity of our services</li>
                </ul>
              </div>

              <div className="terms-section" id="fees">
                <h2>5. Fees and Payments</h2>
                
                <h3>5.1 Fees</h3>
                <p>
                  The Client agrees to pay all fees specified in the applicable SOW or Proposal. Fees may be structured as fixed-price, time-and-materials, or recurring subscription fees.
                </p>

                <h3>5.2 Payment Terms</h3>
                <ul>
                  <li>Invoices are due within 15 days of receipt unless otherwise specified</li>
                  <li>Late payments may incur interest at 1.5% per month</li>
                  <li>The Company may suspend services for non-payment</li>
                  <li>All fees are non-refundable except as expressly provided</li>
                </ul>

                <h3>5.3 Taxes</h3>
                <p>
                  The Client is responsible for all taxes, duties, and assessments imposed by any governmental authority in connection with these Services.
                </p>
              </div>

              <div className="terms-section" id="confidentiality">
                <h2>6. Confidentiality</h2>
                <p>
                  Both parties agree to maintain the confidentiality of all proprietary information disclosed during the course of the engagement. Confidential information includes, but is not limited to:
                </p>
                <ul>
                  <li>Business plans and strategies</li>
                  <li>Technical specifications and source code</li>
                  <li>Customer data and financial information</li>
                  <li>Trade secrets and know-how</li>
                </ul>
                <p>
                  This obligation survives the termination of these Terms for a period of three (3) years.
                </p>
              </div>

              <div className="terms-section" id="warranties">
                <h2>7. Warranties and Disclaimers</h2>
                
                <h3>7.1 Company Warranties</h3>
                <p>
                  The Company warrants that:
                </p>
                <ul>
                  <li>Services will be performed in a professional and workmanlike manner</li>
                  <li>Work Product will conform to the specifications in the SOW</li>
                  <li>The Company has the right to enter into these Terms</li>
                </ul>

                <h3>7.2 Disclaimer</h3>
                <p>
                  EXCEPT AS EXPRESSLY PROVIDED IN THIS SECTION, THE SERVICES AND WORK PRODUCT ARE PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE. THE COMPANY SPECIFICALLY DISCLAIMS ALL IMPLIED WARRANTIES, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                </p>
              </div>

              <div className="terms-section" id="limitation">
                <h2>8. Limitation of Liability</h2>
                <div className="liability-notice">
                  <FaExclamationCircle className="notice-icon" />
                  <p>
                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL THE COMPANY BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR BUSINESS OPPORTUNITIES, ARISING OUT OF OR IN CONNECTION WITH THESE TERMS OR THE SERVICES.
                  </p>
                </div>
                <p>
                  The Company's total liability for any claim arising out of these Terms shall not exceed the total fees paid by the Client during the twelve (12) months preceding the claim.
                </p>
              </div>

              <div className="terms-section" id="termination">
                <h2>9. Termination</h2>
                
                <h3>9.1 Termination for Convenience</h3>
                <p>
                  Either party may terminate these Terms or any SOW for convenience upon thirty (30) days written notice to the other party.
                </p>

                <h3>9.2 Termination for Cause</h3>
                <p>
                  Either party may terminate these Terms immediately upon written notice if the other party:
                </p>
                <ul>
                  <li>Materially breaches these Terms and fails to cure within 15 days</li>
                  <li>Becomes insolvent or files for bankruptcy</li>
                  <li>Violates applicable laws or regulations</li>
                </ul>

                <h3>9.3 Effect of Termination</h3>
                <p>
                  Upon termination, the Client shall pay for all Services rendered through the effective date of termination. Sections regarding intellectual property, confidentiality, and limitation of liability shall survive termination.
                </p>
              </div>

              <div className="terms-section" id="governing">
                <h2>10. Governing Law</h2>
                <div className="law-info">
                  <FaGavel className="law-icon" />
                  <p>
                    These Terms shall be governed by and construed in accordance with the laws of the State of California, without regard to its conflict of laws principles. Any legal action arising out of these Terms shall be brought exclusively in the courts of Santa Clara County, California.
                  </p>
                </div>
              </div>

              <div className="terms-section" id="contact">
                <h2>11. Contact Information</h2>
                <p>
                  If you have any questions about these Terms, please contact us at:
                </p>
                <div className="contact-details">
                  <p><strong>ZustandTech</strong></p>
                  <p>64 Gomti Nagar, Lucknow, (UP) 201030</p>
                  <p>Email: legal@ZustandTech.com</p>
                  <p>Phone: +91 (919) 605-4906</p>
                </div>
              </div>

              <div className="terms-acceptance">
                <FaCheckCircle className="accept-icon" />
                <p>
                  By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default TermsPage;